import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _ from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import * as utils from 'common/utils';
import Selection from 'common/components/Chart/components/Selection';
import { chartFeatureDisplayName } from 'common/charts/chartI18n';

import TagChart from '../../../Machines/MachineDetails/MachineCharts/components/TagChart';
import TagInfo from '../molecules/TagInfo';
import { AnnotatedChart, AnnotationModal } from '../../../Annotations';
import { getVisible } from '../../../Annotations/selectors/annotations.selectors';

import * as assetDetailsActions from '../../actions/assetDetails.actions';

class Trend extends Component {
  constructor(props) {
    super(props);
    this.state = {
      points: {},
      selection: {},
      showAnnotationModal: false,
      selectedAnnotation: null
    };

    this.handleAnnotationClick = this.handleAnnotationClick.bind(this);
    this.handleCloseAnnotationModal = this.handleCloseAnnotationModal.bind(this);
  }

  componentDidMount() {
    this.setInitialSelection();
  }

  getLabelAndUnits() {
    const { config, t } = this.props;
    return {
      yTitle:
        chartFeatureDisplayName(t, config.trends_data[0].feature) ||
        utils.CapitalizeEachWord(config.trends_data[0].tag_type),
      yUnit: config.trends_data[0].units,
      speedUnit: config.trends_data[0].speed_units
    };
  }

  mouseoverChart = (selection) => {
    this.setState(prevState => ({
      selection: {
        ...prevState.selection,
        ...selection,
      }
    }));
  }

  setInitialSelection = () => {
    const { config } = this.props;
    if (
      config.trends_data &&
      !config.trends_data.loading &&
      config.trends_data[0] &&
      config.trends_data[0].trend_data &&
      config.trends_data[0].trend_data.length
    ) {
      const data = config.trends_data[0].trend_data[config.trends_data[0].trend_data.length - 1];
      this.setState({
        points: {
          ...data,
          isDate: true,
          ...this.getLabelAndUnits(data)
        },
        selection: {
          ...data,
          isDate: true,
          ...this.getLabelAndUnits(data)
        }
      });
    }
  }

  getSpectrum = (tag_id, config_id, params) => {
    this.props.assetDetailsActions.getTagSpectrum(tag_id, params, config_id);
  }

  getSpectrumDebounced = _.debounce((tag_id, config_id, params) => {
    this.getSpectrum(tag_id, config_id, params);
  }, 500);

  onMouseClick = (selection) => {
    const { ampType, feature, tag, config, measurement_type } = this.props;
    const params = {
      amp_type: ampType,
      feature,
      timestamp: selection.x,
      measurement_type
    };
    this.getSpectrumDebounced(tag.id, config.config_id, params);
    this.setState(prevState => ({
      points: { ...prevState.selection, ...selection },
    }));
  }

  getTimeRangeFromData() {
    const { config } = this.props;

    if (!config.trends_data || !config.trends_data[0] || !config.trends_data[0].trend_data || !config.trends_data[0].trend_data.length) {
      return null;
    }

    const trendData = config.trends_data[0].trend_data;
    const timePoints = trendData.map(point => new Date(point.x));

    return {
      start: new Date(Math.min(...timePoints)),
      end: new Date(Math.max(...timePoints))
    };
  }

  handleAnnotationClick = (annotation) => {
    this.setState({
      showAnnotationModal: true,
      selectedAnnotation: annotation
    });
  }

  handleCloseAnnotationModal = () => {
    this.setState({
      showAnnotationModal: false,
      selectedAnnotation: null
    });
  }

  render() {
    const {
      ampType,
      config,
      feature,
      tag,
      unitSystem,
      annotationsVisible,
      disableAnnotations
    } = this.props;
    const { points } = this.state;

    const timeRange = this.getTimeRangeFromData();

    const tagChartProps = {
      ampType,
      chartName: `configId-${config.config_id}-trend-analyst`,
      color: config.trends_config[0].color,
      contextHeight: '25px',
      data: config.trends_data[0].trend_data,
      displayOutliers: false,
      expanded: true,
      feature,
      height: '70px',
      loading: config.trends_data.loading,
      miniMode: true,
      mouseClick: this.onMouseClick,
      mouseoverChart: this.mouseoverChart,
      points: this.state.points,
      tagId: config.trends_data[0].tag_id,
      title: 'trend',
      type: 'trend',
      unit_system: unitSystem,
      xIsDate: true,
      yTitle: tag.structure_type === 'vector' ? chartFeatureDisplayName(this.props.t, feature) : utils.CapitalizeEachWord(config.trends_data[0].tag_type),
      yUnit: config.trends_data[0].units
    };

    const showAnnotations = annotationsVisible && timeRange && !disableAnnotations;

    const chartComponent = showAnnotations ? (
      <AnnotatedChart
        entityType="tag"
        entityId={tag.id}
        timeRange={timeRange}
        onAnnotationClick={this.handleAnnotationClick}
      >
        <TagChart {...tagChartProps} />
      </AnnotatedChart>
    ) : (
      <TagChart
        {...tagChartProps}
        disableAnnotations={disableAnnotations}
      />
    );

    return (
      <>
        <TagInfo tag={tag} config={config} waveAndSpecSelection={points} chartType="spectrum" />
        {this.state.selection && (
          <Selection
            toFixedDecimalPoints={2}
            data={this.state.selection}
            annotationsVisible={annotationsVisible && !disableAnnotations}
          />
        )}
        {chartComponent}
        {this.state.showAnnotationModal && this.state.selectedAnnotation && (
          <AnnotationModal
            annotation={this.state.selectedAnnotation}
            onClose={this.handleCloseAnnotationModal}
          />
        )}
      </>
    );
  }
}

Trend.propTypes = {
  ampType: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  feature: PropTypes.string.isRequired,
  measurement_type: PropTypes.number,
  tag: PropTypes.object.isRequired,
  unitSystem: PropTypes.number.isRequired,
  annotationsVisible: PropTypes.bool,
  disableAnnotations: PropTypes.bool,
};

Trend.defaultProps = {
  measurement_type: 1,
  annotationsVisible: true,
  disableAnnotations: false
};

const mapStateToProps = state => ({
  annotationsVisible: getVisible(state)
});

const mapDispatchToProps = dispatch => ({
  assetDetailsActions: bindActionCreators(assetDetailsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Trend));
